import omitProps from '@hocs/omit-props';
import { contains, equals, map } from 'ramda';
import { compact } from 'ramda-adjunct';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-refetch';
import { compose, setDisplayName, withHandlers, withProps, withPropsOnChange } from 'recompose';
import { Color } from '../../../constants/Color';
import { FilterName } from '../../../constants/FilterName';
import { Layout } from '../../../constants/Layout';
import { MediaType } from '../../../constants/MediaType';
import { Orientation } from '../../../constants/Orientation';
import { MainSource } from '../../../constants/Source';
import { generateRandomGuid4 } from '../../../critical/utils';
import { FiltersBiClickOrigins } from '../../hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { MediaManagerSDKFolder, MediaManagerSDKResponseData } from '../../media-manager-sdk';
import { getAdapter } from '../../media-manager-sdk/getAdapter';
import { deserializeStringToArray } from '../../string';
import { withSearchAndFiltersAnalytics } from '../bi/withSearchAndFiltersAnalytics';
import { ejectIntl } from '../ejectIntl';
import { withLayoutAndSort } from '../withLayoutAndSort';
import { withMediaManagerSDK } from '../withMediaManagerSDK';
import { withMMGRSettings, withoutMMGRSettings } from '../withMMGRSettings';
import { IMMGRSettingsProps } from '../withMMGRSettings/withMMGRSettings.types';
import {
  IDecodedFolderProps,
  IHandlers,
  IShutterFilters,
  IWithExtendedSourceInner,
  IWithExtendedSourceOuter,
} from './types';
import {
  decodeShutterstockFolderId,
  encodeBiCategory,
  encodeBiType,
  encodeFolderId,
  getShutterstockDefaultMediaType,
} from './withShutterstock.utils';

export const withShutterstock = compose<IWithExtendedSourceInner, IWithExtendedSourceOuter>(
  setDisplayName('withShutterstock'),
  withLayoutAndSort({ layouts: [Layout.Gallery] }),
  withMMGRSettings,
  withProps(({ folderId, mmgrSettings }) => {
    const { tabsList } = mmgrSettings;
    const mediaTypes = deserializeStringToArray<MediaType>(mmgrSettings.mediaType);
    const defaultMediaType = getShutterstockDefaultMediaType(tabsList, mediaTypes);

    const { mediaType, orientation, category, color } = decodeShutterstockFolderId(folderId, defaultMediaType);

    return {
      mediaType,
      orientation,
      category,
      color,
      folderId: encodeFolderId({ mediaType, orientation, category, color }),
    };
  }),
  withMediaManagerSDK({ source: MainSource.Shutterstock }),
  injectIntl,
  connect<any>(({ listFoldersMP, mediaType, folderId, mmgrSettings, intl: { formatMessage } }) => ({
    shutterCategoriesRequest: {
      comparison: mediaType,
      value: () => {
        const shutterstockAdapter = getAdapter(MainSource.Shutterstock, mmgrSettings);
        const privateSiteAdapter = getAdapter(MainSource.SiteFiles, mmgrSettings);
        const correlationIdFromPrivateAdapter = privateSiteAdapter.correlationId.get();

        if (correlationIdFromPrivateAdapter) {
          shutterstockAdapter.correlationId.set(correlationIdFromPrivateAdapter);
        } else {
          shutterstockAdapter.correlationId.randomize();
        }

        return listFoldersMP(folderId);
      },
      then: (response: MediaManagerSDKResponseData<MediaManagerSDKFolder[]>) => ({
        value: map<MediaManagerSDKFolder, MediaManagerSDKFolder>(
          (entry) => ({
            ...entry,
            name: formatMessage({ id: `shutterstock.category.${entry.id}`, defaultMessage: entry.name }),
          }),
          response.data,
        ) as any,
      }),
    },
  })),
  withProps(({ shutterCategoriesRequest }) => {
    const shutterCategories = shutterCategoriesRequest.fulfilled ? shutterCategoriesRequest.value : [];

    return {
      shutterCategories,
      areShutterstockCategoriesLoading: shutterCategoriesRequest.pending,
    };
  }),
  withPropsOnChange(['mmgrSettings'], ({ mmgrSettings: { tabsList }, intl: { formatMessage } }) => ({
    shutterMediaTypes: compact([
      contains(MainSource.Shutterstock, tabsList) && {
        id: MediaType.Picture,
        mediaType: MediaType.Picture,
        name: formatMessage({ id: 'shutterstock.media-type.picture' }),
      },
      contains(MainSource.ShutterstockVideo, tabsList) && {
        id: MediaType.Video,
        mediaType: MediaType.Video,
        name: formatMessage({ id: 'shutterstock.media-type.video' }),
      },
    ]),
    shutterOrientations: [
      { id: Orientation.All, name: formatMessage({ id: 'orientation.all' }) },
      { id: Orientation.Horizontal, name: formatMessage({ id: 'orientation.horizontal' }) },
      { id: Orientation.Vertical, name: formatMessage({ id: 'orientation.vertical' }) },
    ],
  })),
  withSearchAndFiltersAnalytics,
  withHandlers({
    setMediaType:
      ({
        openFolderById,
        mediaType: currentMediaType,
        orientation,
        color,
        trackSearchAndFiltersStarted,
        shutterCategories,
      }) =>
      (mediaType) => {
        if (mediaType !== currentMediaType) {
          const category = 'any';

          openFolderById(
            encodeFolderId({
              color,
              mediaType,
              orientation,
              category,
            }),
          );
          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Type],
            target: mediaType,
            filterOrientation: orientation,
            filterCategory: encodeBiCategory(category, shutterCategories),
            filterType: encodeBiType(mediaType),
          });
        }
      },
    setCategory:
      ({
        openFolderById,
        mediaType,
        category: currentCategory,
        orientation,
        color,
        trackSearchAndFiltersStarted,
        shutterCategories,
      }) =>
      (category) => {
        if (category !== currentCategory) {
          openFolderById(
            encodeFolderId({
              color,
              mediaType,
              orientation,
              category,
            }),
          );
          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Category],
            target: category,
            filterOrientation: orientation,
            filterCategory: encodeBiCategory(category, shutterCategories),
            filterType: encodeBiType(mediaType),
          });
        }
      },
    setColor:
      ({
        openFolderById,
        mediaType,
        orientation,
        category,
        color: currentColor,
        trackSearchAndFiltersStarted,
        shutterCategories,
      }) =>
      (color) => {
        if (color !== currentColor) {
          openFolderById(
            encodeFolderId({
              color,
              mediaType,
              orientation,
              category,
            }),
          );

          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Color],
            target: color,
            filterOrientation: orientation,
            filterCategory: encodeBiCategory(category, shutterCategories),
            filterType: encodeBiType(mediaType),
          });
        }
      },
    setOrientation:
      ({
        openFolderById,
        mediaType,
        color,
        category,
        orientation: currentOrientation,
        trackSearchAndFiltersStarted,
        shutterCategories,
      }) =>
      (orientation) => {
        if (orientation !== currentOrientation) {
          openFolderById(
            encodeFolderId({
              color,
              mediaType,
              orientation,
              category,
            }),
          );
          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Orientation],
            target: orientation,
            filterOrientation: orientation,
            filterCategory: encodeBiCategory(category, shutterCategories),
            filterType: encodeBiType(mediaType),
          });
        }
      },
    clearAllExtendedFilters:
      ({ openFolderById, mediaType }) =>
      () => {
        openFolderById(encodeFolderId({ mediaType }));
      },
  }),
  withPropsOnChange<
    Partial<IWithExtendedSourceInner>,
    IShutterFilters & IMMGRSettingsProps & WrappedComponentProps & IHandlers & IDecodedFolderProps
  >(
    ['mmgrSettings', 'mediaType', 'orientation', 'color', 'category', 'shutterCategories'],
    ({
      shutterMediaTypes,
      shutterCategories,
      shutterOrientations,
      setMediaType,
      setCategory,
      setOrientation,
      setColor,
      mediaType,
      orientation,
      color,
      category,
      intl: { formatMessage },
    }) => ({
      demonstrationThumbnailStyle: {
        backgroundSize: 'contain',
      },
      itemsFilters: [
        {
          name: FilterName.Type,
          activeItemId: mediaType,
          menuTitle: formatMessage({ id: 'shutterstock.filter.media-type.title' }),
          menuItems: shutterMediaTypes,
          columnsCount: 1,
          onItemClick: (typeId: MediaType) => () => setMediaType(typeId),
        },
        {
          name: FilterName.Category,
          activeItemId: category,
          menuTitle: formatMessage({ id: 'shutterstock.filter.category.title' }),
          menuItems: shutterCategories,
          columnsCount: 2,
          onItemClick: (category) => () => setCategory(category),
        },
        ...(equals(mediaType, MediaType.Picture)
          ? [
              {
                name: FilterName.Orientation,
                activeItemId: orientation,
                activeItemLabel:
                  orientation === Orientation.All ? formatMessage({ id: 'orientation.orientation' }) : null,
                menuTitle: formatMessage({ id: 'shutterstock.filter.orientation.title' }),
                menuItems: shutterOrientations,
                columnsCount: 1,
                onItemClick: (orientation) => () => setOrientation(orientation),
              },
            ]
          : []),
        ...(equals(mediaType, MediaType.Picture)
          ? [
              {
                name: FilterName.Color,
                activeItemId: color,
                activeItemLabel: formatMessage({ id: 'filter.color.color' }),
                menuTitle: formatMessage({ id: 'filter.color.title' }),
                options: {
                  isAnyColorFilterVisible: true,
                  isBlackWhiteFilterVisible: false,
                  customColors: [
                    Color.ShutterstockRed,
                    Color.ShutterstockOrange,
                    Color.ShutterstockAmber,
                    Color.ShutterstockYellow,
                    Color.ShutterstockLime,
                    Color.ShutterstockGreen,
                    Color.ShutterstockTeal,
                    Color.ShutterstockTurquoise,
                    Color.ShutterstockAqua,
                    Color.ShutterstockAzure,
                    Color.Blue,
                    Color.ShutterstockPurple,
                    Color.ShutterstockOrchid,
                    Color.ShutterstockMagenta,
                  ],
                },
                onItemClick: (color: Color) => () => setColor(color),
              },
            ]
          : []),
      ],
      title: formatMessage({ id: 'shutterstock.title' }),
    }),
  ),
  withoutMMGRSettings,
  ejectIntl,
  omitProps('mediaType', 'orientation', 'category', 'shutterCategoriesRequest'),
);
